import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  Palette,
  Sparkles,
  Check,
  RotateCcw,
  LayoutTemplate,
  Sliders,
  Eye,
  Building,
  CheckCircle2,
  Info,
  Edit3,
  X,
  Compass,
  Lock as LockIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import MainLayout from '../../../../Layout/MainLayout';
import PageContainer from '../../../../components/layout/PageContainer';
import { useAgent } from '../../../../context/agent/AgentContext';
import { extractColorsFromImage } from '../../../../utils/colorExtractor';
import PDFVoucherView from '../Shared/PDFVoucherView';
import PlanGate, { usePlanAccess } from '../../../../components/gating/PlanGate';
import { voucherService } from '../../../../services/vouchers/voucherService';
import { getProxyUrl } from '../../../../utils/assetUtils';
import vouchersData from '../../../../data/vouchers/vouchersData.json';

const PRESET_PALETTES = vouchersData.presetPalettes || [];
const sampleVoucherData = vouchersData.sampleVoucher || {};

const VoucherTemplatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { agent, voucherTheme: contextTheme, saveVoucherTheme } = useAgent() || {};
  const { hasAccess: hasGrowthAccess } = usePlanAccess('growth');

  const returnUrl = location.state?.returnUrl || '/voucher';
  const voucherId = location.state?.voucherId || null;
  const initialPassedTheme = location.state?.currentTheme || null;

  const isFromVoucher = Boolean(
    location.state?.voucherId ||
    location.state?.isFromVoucher ||
    (returnUrl && (returnUrl.includes('create-voucher') || returnUrl.includes('voucher-edit')))
  );
  const isFromList = !isFromVoucher;

  const [isCustom, setIsCustom] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#0d9488');
  const [secondaryColor, setSecondaryColor] = useState('#1d4ed8');
  const [textColor, setTextColor] = useState('#111827');
  const [headerBgColor, setHeaderBgColor] = useState('#ffffff');
  const [includeItinerary, setIncludeItinerary] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const activeTheme = useMemo(() => ({
    isCustom,
    primaryColor: isCustom ? primaryColor : '#0d9488',
    secondaryColor: isCustom ? secondaryColor : '#1d4ed8',
    textColor: isCustom ? textColor : '#111827',
    headerBgColor: isCustom ? headerBgColor : '#ffffff',
    include_itinerary: includeItinerary
  }), [isCustom, primaryColor, secondaryColor, textColor, headerBgColor, includeItinerary]);

  // Responsive scale calculations for Live Preview
  const previewContainerRef = useRef(null);
  const voucherInnerRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.72);
  const [previewHeight, setPreviewHeight] = useState(700);

  // Agent profile details for preview
  const [logo, setLogo] = useState('');
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentPhone, setAgentPhone] = useState('');
  const [agentAddress, setAgentAddress] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');

  useEffect(() => {
    const themeToLoad = initialPassedTheme || contextTheme;
    if (themeToLoad) {
      const isC = Boolean(themeToLoad.isCustom || themeToLoad.is_custom);
      const prim = themeToLoad.primaryColor || themeToLoad.primary_color || '#0d9488';
      const sec = themeToLoad.secondaryColor || themeToLoad.secondary_color || '#1d4ed8';
      const txt = themeToLoad.textColor || themeToLoad.text_color || '#111827';
      const hBg = themeToLoad.headerBgColor || themeToLoad.header_bg_color || '#ffffff';
      const inclItin = themeToLoad.include_itinerary !== undefined
        ? Boolean(themeToLoad.include_itinerary)
        : (themeToLoad.includeItinerary !== undefined ? Boolean(themeToLoad.includeItinerary) : false);
      setIsCustom(isC);
      setIncludeItinerary(inclItin);
      setPrimaryColor(prim);
      setSecondaryColor(sec);
      setTextColor(txt);
      setHeaderBgColor(hBg);
      setSavedSnapshot({
        isCustom: isC,
        primaryColor: prim,
        secondaryColor: sec,
        textColor: txt,
        headerBgColor: hBg,
        include_itinerary: inclItin
      });
    }
  }, [initialPassedTheme, contextTheme]);

  useEffect(() => {
    if (agent) {
      setAgentName(agent.company_name || 'Your Travel Agency');
      setAgentEmail(agent.email || 'info@travelagency.com');
      setAgentPhone(agent.phone_number || '+91 9988776655');
      setAgentAddress(agent.address || '123 Business Avenue');
      setCity(agent.city || 'Mumbai');
      setPincode(agent.pincode || '400001');

      if (agent.logo) {
        const fetchLogo = async () => {
          try {
            const proxyUrl = getProxyUrl(agent.logo);
            const res = await fetch(proxyUrl);
            const blob = await res.blob();
            const reader = new FileReader();
            reader.onloadend = () => setLogo(reader.result);
            reader.readAsDataURL(blob);
          } catch (err) {
            setLogo(agent.logo);
          }
        };
        fetchLogo();
      }
    }
  }, [agent?._id, agent?.logo]);

  // Responsive scale & height updater for the preview container (320px - 1920px)
  useEffect(() => {
    const updateScaleAndHeight = () => {
      if (previewContainerRef.current) {
        const containerWidth = previewContainerRef.current.clientWidth;
        const pad = containerWidth < 400 ? 12 : (containerWidth < 640 ? 20 : 32);
        const availableWidth = Math.max(containerWidth - pad, 200);
        const targetWidth = 794;
        const calculatedScale = Math.min(Math.max(availableWidth / targetWidth, 0.22), 1.15);
        setPreviewScale(calculatedScale);

        if (voucherInnerRef.current) {
          const fullHeight = voucherInnerRef.current.scrollHeight || 900;
          setPreviewHeight(Math.round(fullHeight * calculatedScale));
        }
      }
    };

    updateScaleAndHeight();
    const timer = setTimeout(updateScaleAndHeight, 150);

    window.addEventListener('resize', updateScaleAndHeight);
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && previewContainerRef.current) {
      resizeObserver = new ResizeObserver(updateScaleAndHeight);
      resizeObserver.observe(previewContainerRef.current);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateScaleAndHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [logo, agentName, activeTheme]);

  const handleAutoExtract = async () => {
    if (!logo) {
      toast.error('No agency logo found to extract colors from.');
      return;
    }
    setIsExtracting(true);
    try {
      const colors = await extractColorsFromImage(logo);
      setPrimaryColor(colors.primary);
      setSecondaryColor(colors.secondary);
      if (colors.textColor) setTextColor(colors.textColor);
      if (colors.headerBgColor) setHeaderBgColor(colors.headerBgColor);
      setIsCustom(true);
      toast.success('Complete 4-color brand palette extracted from logo!', { icon: '✨' });
    } catch (err) {
      toast.error('Could not extract colors from logo.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSelectPreset = (preset) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
    setTextColor(preset.text || '#111827');
    setHeaderBgColor(preset.headerBg || '#ffffff');
  };

  const handleResetToDefault = () => {
    setIsCustom(false);
    setPrimaryColor('#0d9488');
    setSecondaryColor('#1d4ed8');
    setTextColor('#111827');
    setHeaderBgColor('#ffffff');
    setIncludeItinerary(true);
    toast.success('Reset to classic default template styling');
  };

  const handleCancelEdit = () => {
    if (savedSnapshot) {
      setIsCustom(savedSnapshot.isCustom);
      setPrimaryColor(savedSnapshot.primaryColor);
      setSecondaryColor(savedSnapshot.secondaryColor);
      setTextColor(savedSnapshot.textColor);
      setHeaderBgColor(savedSnapshot.headerBgColor);
      if (savedSnapshot.include_itinerary !== undefined) {
        setIncludeItinerary(savedSnapshot.include_itinerary);
      }
      if (savedSnapshot.setAsDefault !== undefined) {
        setSetAsDefault(savedSnapshot.setAsDefault);
      }
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const themeConfig = {
      is_custom: isCustom,
      primary_color: isCustom ? primaryColor : '#0d9488',
      secondary_color: isCustom ? secondaryColor : '#1d4ed8',
      text_color: isCustom ? textColor : '#111827',
      header_bg_color: isCustom ? headerBgColor : '#ffffff',
      primaryColor: isCustom ? primaryColor : '#0d9488',
      secondaryColor: isCustom ? secondaryColor : '#1d4ed8',
      textColor: isCustom ? textColor : '#111827',
      headerBgColor: isCustom ? headerBgColor : '#ffffff',
      include_itinerary: includeItinerary
    };

    try {
      if (isFromList || setAsDefault) {
        if (saveVoucherTheme) {
          await saveVoucherTheme(themeConfig);
        }
        if (isFromList) {
          toast.success('Template saved as default for all new vouchers!');
        } else {
          toast.success('Template applied & set as global default!');
        }
      }

      if (isFromVoucher && voucherId) {
        try {
          await voucherService.updateVoucher(voucherId, {
            voucherData: JSON.stringify({ voucher_theme: themeConfig })
          });
        } catch (vErr) {
          console.warn('Could not save directly to voucher record:', vErr);
        }
      }

      if (isFromVoucher && !setAsDefault) {
        toast.success('Template applied to this voucher!');
      }

      navigate(returnUrl, {
        state: {
          ...location.state,
          activeTab: location.state?.activeTab || 'manual',
          customTheme: themeConfig,
          voucherTheme: themeConfig
        }
      });
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template preferences.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainLayout activeTab="voucher">
      <div className="bg-slate-50 min-h-screen">
        <PageContainer>
          {/* Header Navigation */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 sm:pb-6 border-b border-slate-200">
            <div className="min-w-0">
              <button
                onClick={() => navigate(returnUrl, {
                  state: {
                    ...location.state,
                    activeTab: location.state?.activeTab || 'manual'
                  }
                })}
                className="flex items-center gap-1.5 text-slate-500 hover:text-teal-700 font-bold text-xs sm:text-sm mb-2 transition-colors group cursor-pointer"
              >
                <ChevronLeft size={18} className="transition-transform group-hover:-translate-x-0.5" />
                <span>Back</span>
              </button>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-[#067978] tracking-tight uppercase leading-tight">
                Voucher Template
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-2xl leading-relaxed">
                Customize your voucher colors, branding, and headers according to your agency logo.
              </p>
            </div>

            <div className="w-full sm:w-auto shrink-0">
              {!isEditing ? (
                <PlanGate
                  requiredPlan="growth"
                  featureName="Voucher Template Customization"
                  tooltipText="Customizing voucher templates requires the Growth Plan."
                  showLockIcon={false}
                >
                  {({ isLocked }) => (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="w-full sm:w-auto px-5 py-2.5 bg-[#067978] hover:bg-[#056665] text-white rounded-xl font-bold text-xs shadow-md shadow-teal-900/15 transition-all active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <Edit3 size={15} />
                      <span>Edit Template</span>
                      {isLocked && <LockIcon size={14} className="ml-1 text-white/90" />}
                    </button>
                  )}
                </PlanGate>
              ) : (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="w-full sm:w-auto px-4 py-2 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-xl font-bold text-xs shadow-xs transition-all flex items-center justify-center gap-1.5 active:scale-95 whitespace-nowrap cursor-pointer"
                >
                  <X size={14} />
                  <span>Cancel Editing</span>
                </button>
              )}
            </div>
          </div>

          {/* Growth Plan Notice Banner when on lower tier */}
          {!hasGrowthAccess && (
            <div className="mb-6 p-4 sm:p-5 bg-teal-50/60 border border-teal-200/90 rounded-2xl sm:rounded-3xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xs">
              <div className="flex items-start gap-3.5">
                <div className="w-9 h-9 rounded-xl bg-teal-100/80 text-[#067978] flex items-center justify-center font-bold shrink-0 mt-0.5 shadow-2xs">
                  <LockIcon size={18} />
                </div>
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-slate-800">
                    Default Template & Itinerary Override Active
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5 leading-relaxed">
                    Custom voucher branding and itinerary inclusion require the <strong className="text-[#067978]">Growth Plan</strong>. Your custom palette is safely saved, but vouchers & PDFs are currently served with the <strong>Default Trrip Template</strong> (itinerary hidden) until you upgrade.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="px-4 py-2 bg-[#067978] hover:bg-[#056362] text-white rounded-xl font-bold text-xs shadow-xs transition-all active:scale-95 shrink-0 whitespace-nowrap cursor-pointer"
              >
                Upgrade to Growth
              </button>
            </div>
          )}

          {/* Main 2-Column Responsive Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            {/* Left Column: Controls & Configuration */}
            <div className="lg:col-span-5 space-y-4 sm:space-y-6 w-full">
              {!isEditing ? (
                /* View Mode: Active Template Summary Card */
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-7 border border-slate-200/80 shadow-xs space-y-5 sm:space-y-6"
                >
                  {/* Card Header */}
                  <div className="border-b border-slate-100 pb-4 space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-teal-50 text-[#067978] flex items-center justify-center font-bold shrink-0 shadow-xs">
                        <LayoutTemplate size={18} />
                      </div>
                      <span className={`text-[10px] sm:text-[11px] font-bold px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full shrink-0 border ${
                        !hasGrowthAccess
                          ? 'bg-slate-100 text-slate-700 border-slate-200'
                          : isCustom
                            ? 'bg-teal-50 text-[#067978] border-teal-200/80'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {!hasGrowthAccess
                          ? 'Default Template Active'
                          : isCustom
                            ? 'Custom Template'
                            : 'Trrip Template'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-black text-slate-800 tracking-tight leading-tight">
                        Active Template
                      </h3>
                      <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-snug">
                        {!hasGrowthAccess
                          ? 'Standard Trrip template is currently served because Growth Plan is required'
                          : 'Currently applied to all vouchers'}
                      </p>
                    </div>
                  </div>

                  {/* 4 Selected Colors Grid */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-500">
                        Color Palette {!hasGrowthAccess && isCustom && <span className="text-[#067978] font-normal">(Saved / Paused)</span>}
                      </span>
                      <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-full border border-slate-200/80">
                        <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full shadow-2xs shrink-0" style={{ backgroundColor: primaryColor }} title="Primary Color" />
                        <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full shadow-2xs shrink-0" style={{ backgroundColor: secondaryColor }} title="Secondary Color" />
                        <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full shadow-2xs shrink-0" style={{ backgroundColor: textColor }} title="Font Color" />
                        <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full shadow-2xs shrink-0 border border-slate-300" style={{ backgroundColor: headerBgColor }} title="Header Background" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      {/* Color 1: Primary */}
                      <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl shadow-xs border border-black/5 shrink-0"
                          style={{ backgroundColor: primaryColor }}
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-slate-500">Primary Color</p>
                          <p className="text-xs font-mono font-black text-slate-800 uppercase">{primaryColor}</p>
                          <p className="text-[10px] text-slate-400 truncate">Borders & Highlights</p>
                        </div>
                      </div>

                      {/* Color 2: Secondary */}
                      <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl shadow-xs border border-black/5 shrink-0"
                          style={{ backgroundColor: secondaryColor }}
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-slate-500">Secondary Color</p>
                          <p className="text-xs font-mono font-black text-slate-800 uppercase">{secondaryColor}</p>
                          <p className="text-[10px] text-slate-400 truncate">Titles & Section Accents</p>
                        </div>
                      </div>

                      {/* Color 3: Font Color */}
                      <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl shadow-xs border border-black/5 shrink-0"
                          style={{ backgroundColor: textColor }}
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-slate-500">Information Font</p>
                          <p className="text-xs font-mono font-black text-slate-800 uppercase">{textColor}</p>
                          <p className="text-[10px] text-slate-400 truncate">Body text & details</p>
                        </div>
                      </div>

                      {/* Color 4: Header Background */}
                      <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl shadow-xs border border-slate-200 shrink-0"
                          style={{ backgroundColor: headerBgColor }}
                        />
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-slate-500">Header Background</p>
                          <p className="text-xs font-mono font-black text-slate-800 uppercase">{headerBgColor}</p>
                          <p className="text-[10px] text-slate-400 truncate">Top banner background</p>
                        </div>
                      </div>
                    </div>

                    {/* Trip Itinerary Setting Status */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-2">
                        <Compass size={15} className="text-[#067978]" />
                        <span className="text-xs font-bold text-slate-700">Trip Itinerary</span>
                      </div>
                      <span className={`text-[10px] sm:text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${
                        !hasGrowthAccess
                          ? 'bg-slate-100 text-slate-600 border-slate-200'
                          : includeItinerary
                            ? 'bg-teal-50 text-[#067978] border-teal-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>
                        {!hasGrowthAccess
                          ? 'Hidden (Growth Required)'
                          : includeItinerary
                            ? 'Included in Voucher'
                            : 'Hidden from Voucher'}
                      </span>
                    </div>
                  </div>

                  {/* Edit Action Button */}
                  <div className="pt-2">
                    <PlanGate
                      requiredPlan="growth"
                      featureName="Voucher Template Customization"
                      tooltipText="Customizing voucher templates requires the Growth Plan."
                      showLockIcon={false}
                      wrapperClassName="w-full block"
                    >
                      {({ isLocked }) => (
                        <button
                          type="button"
                          onClick={() => setIsEditing(true)}
                          className="w-full py-3.5 bg-[#067978] hover:bg-[#056362] text-white rounded-2xl font-bold text-xs shadow-md shadow-teal-900/10 transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <Edit3 size={16} />
                          <span>Edit Template</span>
                          {isLocked && <LockIcon size={14} className="ml-1 text-white/90" />}
                        </button>
                      )}
                    </PlanGate>
                  </div>
                </motion.div>
              ) : (
                /* Edit Mode: Tools and Pickers */
                <>
                  {/* Set as Default Option Card */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 border border-slate-200/80 shadow-xs flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl flex items-center justify-center font-bold text-sm sm:text-base shrink-0 transition-colors ${setAsDefault ? 'bg-[#067978] text-white shadow-xs' : 'bg-slate-100 text-slate-500'
                        }`}>
                        ★
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight truncate">
                          Set as Default Template
                        </h4>
                        <p className="text-[11px] sm:text-xs text-slate-500 leading-snug">
                          {isFromVoucher
                            ? 'Also save as global default for future vouchers'
                            : 'Save as the global default for all newly created vouchers'}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={setAsDefault}
                        onChange={(e) => setSetAsDefault(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#067978]"></div>
                    </label>
                  </div>

                  {/* Include Trip Itinerary Toggle Card */}
                  <PlanGate
                    requiredPlan="growth"
                    featureName="Trip Itinerary in Voucher PDF"
                    tooltipText="Customizing whether the trip itinerary is included in the voucher PDF requires the Growth Plan."
                    showLockIcon={false}
                    wrapperClassName="w-full block"
                  >
                    {({ isLocked, onClick }) => {
                      const isChecked = !isLocked && includeItinerary;
                      return (
                        <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 border border-slate-200/80 shadow-xs flex items-center justify-between gap-3 w-full">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold text-sm sm:text-base shrink-0">
                              <Compass size={18} />
                            </div>
                            <div className="min-w-0 text-left">
                              <h4 className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight truncate">
                                Include Trip Itinerary
                              </h4>
                              <p className="text-[11px] sm:text-xs text-slate-500 leading-snug">
                                Display day-by-day trip itinerary in the voucher PDF
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={isLocked ? onClick : () => setIncludeItinerary(!includeItinerary)}
                            className={`w-11 h-6 rounded-full transition-colors relative flex items-center shrink-0 cursor-pointer ${
                              isChecked ? 'bg-[#067978]' : 'bg-slate-200'
                            }`}
                            title={isLocked ? 'Upgrade to Growth Plan to include itinerary in voucher PDF' : undefined}
                          >
                            <span className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform shadow-xs flex items-center justify-center ${
                              isChecked ? 'right-0.5' : 'left-0.5'
                            }`}>
                              {isLocked && <LockIcon size={10} className="text-slate-500" />}
                            </span>
                          </button>
                        </div>
                      );
                    }}
                  </PlanGate>

                  {/* Template Mode Switcher */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold shrink-0">
                        <LayoutTemplate size={18} />
                      </div>
                      <div>
                        <h3 className="text-xs sm:text-sm font-bold text-slate-800">Template Mode</h3>
                        <p className="text-[11px] sm:text-xs text-slate-500">Choose between Trrip or custom template</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => setIsCustom(false)}
                        className={`p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border-2 text-left transition-all relative cursor-pointer ${!isCustom
                          ? 'border-teal-600 bg-teal-50/50 shadow-xs ring-2 ring-teal-500/20'
                          : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-bold text-slate-800">Trrip Template</span>
                          {!isCustom && <CheckCircle2 size={16} className="text-teal-600 shrink-0" />}
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug">
                          Classic Trrip teal & sapphire blue palette.
                        </p>
                        <div className="flex items-center -space-x-1.5 mt-3">
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-40 shrink-0 bg-[#0d9488]" title="Primary: #0d9488"></div>
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-30 shrink-0 bg-[#1d4ed8]" title="Secondary: #1d4ed8"></div>
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-20 shrink-0 bg-[#111827]" title="Information Font: #111827"></div>
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-10 shrink-0 bg-white border border-slate-200" title="Header BG: #ffffff"></div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsCustom(true)}
                        className={`p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border-2 text-left transition-all relative cursor-pointer ${isCustom
                          ? 'border-teal-600 bg-teal-50/50 shadow-xs ring-2 ring-teal-500/20'
                          : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-bold text-slate-800">Custom Template</span>
                          {isCustom && <CheckCircle2 size={16} className="text-teal-600 shrink-0" />}
                        </div>
                        <p className="text-[11px] text-slate-500 leading-snug">
                          Matched with agency logo & custom colors.
                        </p>
                        <div className="flex items-center -space-x-1.5 mt-3">
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-40 shrink-0" style={{ backgroundColor: primaryColor }} title="Primary"></div>
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-30 shrink-0" style={{ backgroundColor: secondaryColor }} title="Secondary"></div>
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-20 shrink-0" style={{ backgroundColor: textColor }} title="Information Font"></div>
                          <div className="w-5 h-5 rounded-full ring-2 ring-white shadow-xs z-10 shrink-0 border border-slate-200" style={{ backgroundColor: headerBgColor }} title="Header Background"></div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Logo Auto-Extract Card */}
                  {isCustom && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-teal-50 text-[#067978] flex items-center justify-center font-bold shrink-0">
                            <Sparkles size={18} />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-xs sm:text-sm font-bold text-slate-800">
                              Auto-Extract from Logo
                            </h3>
                            <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-relaxed">
                              Automatically extract 4 dominant colors from your agency logo
                            </p>
                          </div>
                        </div>

                        {logo ? (
                          <div className="w-11 h-11 sm:w-12 sm:h-12 bg-slate-50 rounded-xl sm:rounded-2xl p-1.5 flex items-center justify-center shrink-0 border border-slate-200">
                            <img src={logo} alt="Agency Logo" className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-11 h-11 sm:w-12 sm:h-12 bg-slate-50 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 border border-slate-200">
                            <Building size={18} className="text-slate-400" />
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={handleAutoExtract}
                        disabled={isExtracting || !logo}
                        className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#056362] text-white rounded-xl font-bold text-xs shadow-xs transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                      >
                        {isExtracting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Extracting Colors...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} />
                            <span>Extract Colors from Logo</span>
                          </>
                        )}
                      </button>
                    </motion.div>
                  )}

                  {/* Custom Color Pickers */}
                  {isCustom && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold shrink-0">
                          <Sliders size={18} />
                        </div>
                        <div>
                          <h3 className="text-xs sm:text-sm font-bold text-slate-800">Custom Color Pickers</h3>
                          <p className="text-[11px] sm:text-xs text-slate-500">Fine-tune individual theme colors</p>
                        </div>
                      </div>

                      {/* Primary Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                          <span>Primary Color</span>
                          <span className="text-[10px] text-slate-400 font-normal">Header border, booking ID, office address label</span>
                        </label>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <input
                            type="color"
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl cursor-pointer border border-slate-200 bg-white p-0.5 shrink-0"
                          />
                          <input
                            type="text"
                            value={primaryColor}
                            onChange={(e) => setPrimaryColor(e.target.value)}
                            className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs sm:text-sm font-bold text-slate-800 uppercase outline-none focus:ring-2 focus:ring-teal-500/20"
                            placeholder="#0D9488"
                          />
                        </div>
                      </div>

                      {/* Secondary Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                          <span>Secondary Accent Color</span>
                          <span className="text-[10px] text-slate-400 font-normal">Section titles, headings, badges</span>
                        </label>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <input
                            type="color"
                            value={secondaryColor}
                            onChange={(e) => setSecondaryColor(e.target.value)}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl cursor-pointer border border-slate-200 bg-white p-0.5 shrink-0"
                          />
                          <input
                            type="text"
                            value={secondaryColor}
                            onChange={(e) => setSecondaryColor(e.target.value)}
                            className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs sm:text-sm font-bold text-slate-800 uppercase outline-none focus:ring-2 focus:ring-teal-500/20"
                            placeholder="#1D4ED8"
                          />
                        </div>
                      </div>

                      {/* Text / Information Font Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-700 flex items-center justify-between flex-wrap gap-1">
                          <span>Information Font Color</span>
                          <span className="text-[10px] text-slate-400 font-normal">Guest details, hotels, notes, text values</span>
                        </label>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <input
                            type="color"
                            value={textColor}
                            onChange={(e) => setTextColor(e.target.value)}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl cursor-pointer border border-slate-200 bg-white p-0.5 shrink-0"
                          />
                          <input
                            type="text"
                            value={textColor}
                            onChange={(e) => setTextColor(e.target.value)}
                            className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs sm:text-sm font-bold text-slate-800 uppercase outline-none focus:ring-2 focus:ring-teal-500/20"
                            placeholder="#111827"
                          />
                        </div>
                      </div>

                      {/* Header Background Color */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-700 flex items-center justify-between flex-wrap gap-1">
                          <span>Header Background Color</span>
                          <span className="text-[10px] text-slate-400 font-normal">Top header background before divider line</span>
                        </label>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <input
                            type="color"
                            value={headerBgColor}
                            onChange={(e) => setHeaderBgColor(e.target.value)}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl cursor-pointer border border-slate-200 bg-white p-0.5 shrink-0"
                          />
                          <input
                            type="text"
                            value={headerBgColor}
                            onChange={(e) => setHeaderBgColor(e.target.value)}
                            className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs sm:text-sm font-bold text-slate-800 uppercase outline-none focus:ring-2 focus:ring-teal-500/20"
                            placeholder="#FFFFFF"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Preset Palettes */}
                  {isCustom && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold shrink-0">
                          <Palette size={18} />
                        </div>
                        <div>
                          <h3 className="text-xs sm:text-sm font-bold text-slate-800">Curated Brand Palettes</h3>
                          <p className="text-[11px] sm:text-xs text-slate-500">Pick from pre-tested color combinations</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-2.5 sm:gap-3 pt-1">
                        {PRESET_PALETTES.map((preset, idx) => {
                          const isSelected = primaryColor === preset.primary && secondaryColor === preset.secondary && textColor === preset.text && headerBgColor === preset.headerBg;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectPreset(preset)}
                              className={`p-3 sm:p-3.5 rounded-xl sm:rounded-2xl border text-left transition-all cursor-pointer ${isSelected
                                ? 'border-teal-600 bg-teal-50/70 ring-2 ring-teal-500/20 shadow-xs'
                                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50'
                                }`}
                            >
                              <div className="flex items-center -space-x-1.5 mb-2.5">
                                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full ring-2 ring-white shadow-xs z-40 shrink-0 inline-block" style={{ backgroundColor: preset.primary }} title="Primary"></div>
                                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full ring-2 ring-white shadow-xs z-30 shrink-0 inline-block" style={{ backgroundColor: preset.secondary }} title="Secondary"></div>
                                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full ring-2 ring-white shadow-xs z-20 shrink-0 inline-block" style={{ backgroundColor: preset.text }} title="Information Font"></div>
                                <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full ring-2 ring-white shadow-xs z-10 shrink-0 border border-slate-200 inline-block" style={{ backgroundColor: preset.headerBg }} title="Header BG"></div>
                              </div>
                              <p className="text-xs font-bold text-slate-800 tracking-tight leading-tight">{preset.name}</p>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* Bottom Actions Card for Saving Template */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs space-y-3.5"
                  >
                    {/* Default Setting Checkbox (if editing for specific voucher) */}
                    {isFromVoucher && (
                      <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                        <input
                          type="checkbox"
                          id="setAsDefaultCheckbox"
                          checked={setAsDefault}
                          onChange={(e) => setSetAsDefault(e.target.checked)}
                          className="w-4 h-4 text-[#067978] rounded border-slate-300 focus:ring-[#067978] cursor-pointer"
                        />
                        <label htmlFor="setAsDefaultCheckbox" className="text-xs text-slate-600 font-medium cursor-pointer select-none">
                          Set as default template for all future vouchers
                        </label>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isSaving}
                      className="w-full py-3 sm:py-3.5 bg-[#067978] hover:bg-[#056665] text-white rounded-xl font-bold text-xs sm:text-sm shadow-md shadow-teal-900/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer active:scale-98"
                    >
                      {isSaving ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Check size={17} />
                      )}
                      <span>Save & Apply Template</span>
                    </button>

                    <div className="flex items-center justify-center gap-4 pt-0.5">
                      <button
                        type="button"
                        onClick={handleResetToDefault}
                        className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1.5 cursor-pointer py-1"
                      >
                        <RotateCcw size={13} />
                        <span>Reset</span>
                      </button>
                      <span className="text-slate-300">·</span>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="text-xs font-medium text-slate-500 hover:text-rose-600 transition-colors flex items-center gap-1.5 cursor-pointer py-1"
                      >
                        <X size={13} />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </div>

            {/* Right Column: Live Interactive Voucher Preview */}
            <div className="lg:col-span-7 lg:sticky lg:top-8 space-y-4 w-full">
              <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Eye size={18} className="text-teal-600 shrink-0" />
                    <h3 className="text-xs sm:text-sm font-bold text-slate-800">
                      Live Voucher Preview
                    </h3>
                  </div>
                  <span className={`text-[10px] sm:text-[11px] font-bold px-2.5 py-0.5 sm:py-1 rounded-full shrink-0 border ${
                    !hasGrowthAccess
                      ? 'bg-slate-100 text-slate-700 border-slate-200'
                      : isCustom
                        ? 'bg-teal-50 text-[#067978] border-teal-200/80'
                        : 'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>
                    {!hasGrowthAccess
                      ? 'Default Template Preview (Growth Required)'
                      : isCustom
                        ? 'Custom Template Active'
                        : 'Trrip Template Active'}
                  </span>
                </div>

                {/* PDF Voucher View Live Scaled Container */}
                <div
                  ref={previewContainerRef}
                  className="bg-slate-100/70 p-2 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200 overflow-hidden flex justify-center items-start w-full"
                  style={{
                    minHeight: previewHeight ? `${previewHeight}px` : 'auto'
                  }}
                >
                  <div
                    style={{
                      width: `${Math.round(previewScale * 794)}px`,
                      height: previewHeight ? `${previewHeight}px` : 'auto',
                      overflow: 'hidden',
                      position: 'relative',
                      maxWidth: '100%'
                    }}
                  >
                    <div
                      ref={voucherInnerRef}
                      className="bg-white shadow-xl rounded-xl overflow-hidden pointer-events-none"
                      style={{
                        width: '794px',
                        transform: `scale(${previewScale})`,
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0
                      }}
                    >
                      <PDFVoucherView
                        data={sampleVoucherData}
                        bookingId={sampleVoucherData?.booking_id || 'bysid'}
                        logo={logo}
                        agentName={agentName}
                        agentEmail={agentEmail}
                        agentPhone={agentPhone}
                        agentAddress={agentAddress}
                        city={city}
                        pincode={pincode}
                        customTheme={activeTheme}
                      />
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-teal-50/50 rounded-xl border border-teal-100/80 flex items-center gap-2.5 text-[11px] sm:text-xs text-teal-800 leading-relaxed">
                  <Info size={16} className="shrink-0 text-teal-600" />
                  <span>
                    The default layout, font hierarchy, and 24-hour time standards remain strictly preserved while your brand palette is seamlessly applied.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    </MainLayout>
  );
};

export default VoucherTemplatePage;
